import { useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  useImportWizard,
  useCampaignCapabilitiesStore,
  type ImportWizardStep,
  type ImportBatchApi,
} from '@discovr/features';
import { ImportUploadStep } from './ImportUploadStep';
import { ImportUploadReviewStep } from './ImportUploadReviewStep';
import { ImportMappingStep } from './ImportMappingStep';
import { ImportSheetIssuesStep } from './ImportSheetIssuesStep';
import { ImportRosterConflictsStep } from './ImportRosterConflictsStep';
import { ImportConfirmStep } from './ImportConfirmStep';
import { ImportProgressBar } from './ImportProgressBar';
import { ImportResultsPanel } from './ImportResultsPanel';
import { ImportStepIndicator } from './ImportStepIndicator';
import { ImportWizardFooterProvider } from './ImportWizardFooterSlot';

const STEP_TITLES: Record<ImportWizardStep, string> = {
  upload: 'Upload Creator Sheet',
  mapping: 'Match your columns',
  'resolve-sheet-issues': 'Resolve sheet issues',
  'resolve-roster-conflicts': 'Resolve roster conflicts',
  confirm: 'Confirm import',
  importing: 'Importing creators',
  done: 'Import complete',
};

interface CreatorImportWizardProps {
  open: boolean;
  campaignId: string;
  api: ImportBatchApi;
  payoutModel?: string;
  /** Gates the "Needs fixing" editor's username wrong-platform-link live
   *  check — see ImportSheetIssuesStep/clientFieldError. */
  campaignPlatform?: string;
  onClose: () => void;
}

export function CreatorImportWizard({ open, campaignId, api, payoutModel, campaignPlatform, onClose }: CreatorImportWizardProps) {
  const wizard = useImportWizard(campaignId, api);

  const importProgress = useCampaignCapabilitiesStore((s) => s.importProgress);
  const importResult = useCampaignCapabilitiesStore((s) => s.importResult);

  useEffect(() => {
    if (wizard.step === 'importing' && importResult) wizard.markDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `wizard` (the whole object) is a new reference every render; depending on it would re-run this effect every render instead of only on the step/result transitions it actually cares about. `wizard.markDone` is itself a stable useCallback identity.
  }, [wizard.step, importResult, wizard.markDone]);

  // Checks for an active import (and fetches expected columns) the instant
  // the wizard opens — before any file is chosen — so a campaign that's
  // already mid-import shows the "Discard and try again" card immediately
  // instead of only after a wasted file-pick-and-upload round trip. `reset()`
  // (run by handleClose on every close) clears the wizard back to `upload`
  // each time, so re-opening always re-fires this check.
  useEffect(() => {
    if (open) wizard.checkStatus().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-check on open/close, not every render (see the effect above for why `wizard` itself isn't a dep).
  }, [open]);

  const handleClose = () => {
    void wizard.closeAndDiscard();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !wizard.busy) handleClose(); }}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[600px] max-h-[calc(100vh-4rem)] w-3xl max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-2xl p-0"
      >
        <div className="flex shrink-0 flex-col gap-3 border-b border-border px-6 pt-4 pb-3">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-heading-h7 text-foreground">{STEP_TITLES[wizard.step]}</DialogTitle>
              <DialogDescription className="sr-only">Staged creator sheet import review</DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={handleClose}
              disabled={wizard.busy}
              aria-label="Close"
              className="size-9 rounded-lg border border-border text-muted-foreground hover:bg-accent"
            >
              <X size={18} />
            </Button>
          </div>
          <ImportStepIndicator step={wizard.step} rows={wizard.rows} />
        </div>

        <ImportWizardFooterProvider>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-5">
          {wizard.error && !wizard.activeConflictImportId && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-600">
              {wizard.error}
            </div>
          )}

          {wizard.step === 'upload' && (
            wizard.checkingActive ? (
              <div className="flex items-center justify-center py-12">
                <span className="size-5 animate-spin rounded-full border-2 border-border border-t-foreground" />
              </div>
            ) : wizard.activeConflictImportId ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <p className="text-body-sm-semibold text-foreground">
                  {wizard.resumableBatch ? 'You have an import in progress' : 'An import is already in progress for this campaign'}
                </p>
                <p className="text-[12px] text-muted-foreground">
                  {wizard.resumableBatch ? (
                    <>
                      <span className="font-medium text-foreground">{wizard.resumableBatch.fileName}</span>
                      {wizard.resumableBatch.uploadedByName && (
                        <> — started by <span className="font-medium text-foreground">{wizard.resumableBatch.uploadedByName}</span></>
                      )}
                      {' '}— still at {wizard.resumableStep ? STEP_TITLES[wizard.resumableStep] : 'review'}. Pick up where you
                      left off, or discard it and start fresh.
                    </>
                  ) : (
                    <>
                      This could be another admin uploading right now, another tab, or an import left over from a closed
                      session. Discard it to start a fresh upload — anything already reviewed in it will be lost.
                    </>
                  )}
                </p>
                <div className="flex gap-2">
                  {wizard.resumableBatch && (
                    <Button
                      variant="default"
                      size="sm"
                      type="button"
                      disabled={wizard.busy}
                      onClick={wizard.resumeActiveImport}
                    >
                      Resume import
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    disabled={wizard.busy}
                    onClick={() => { wizard.discardActiveConflict().catch(() => {}); }}
                  >
                    Discard and try again
                  </Button>
                </div>
              </div>
            ) : wizard.importId ? (
              // Revisiting Upload via Back from Column Mapping — the sheet is
              // already on the server (still unsubmitted, batch status
              // `mapping`), so this is a read-only recap, not a fresh dropzone.
              <ImportUploadReviewStep
                fileName={wizard.fileName ?? ''}
                rowCount={wizard.rowCount}
                sheetColumns={wizard.sheetColumns}
                busy={wizard.busy}
                onContinue={wizard.continueToMapping}
                onChangeFile={wizard.changeFile}
              />
            ) : (
              <ImportUploadStep
                payoutModel={payoutModel}
                expectedFields={wizard.expectedFields}
                busy={wizard.busy}
                onUpload={wizard.uploadFile}
              />
            )
          )}

          {wizard.step === 'mapping' && (
            <ImportMappingStep
              sheetColumns={wizard.sheetColumns}
              expectedFields={wizard.expectedFields}
              rowCount={wizard.rowCount}
              busy={wizard.busy}
              onBack={wizard.backToUpload}
              onSubmit={wizard.submitMapping}
            />
          )}

          {wizard.step === 'resolve-sheet-issues' && (
            <ImportSheetIssuesStep
              rows={wizard.rows}
              expectedFields={wizard.expectedFields}
              campaignPlatform={campaignPlatform}
              busy={wizard.busy}
              onResolve={wizard.resolveSheetIssue}
              onBack={wizard.backToMapping}
              onContinue={wizard.continueToRosterReview}
            />
          )}

          {wizard.step === 'resolve-roster-conflicts' && (
            <ImportRosterConflictsStep
              rows={wizard.rows}
              expectedFields={wizard.expectedFields}
              busy={wizard.busy}
              onResolve={wizard.resolveRosterConflict}
              onBack={wizard.backToSheetIssues}
              onContinue={wizard.continueToConfirm}
            />
          )}

          {wizard.step === 'confirm' && (
            <ImportConfirmStep
              rows={wizard.rows}
              expectedFields={wizard.expectedFields}
              busy={wizard.busy}
              onCommit={wizard.commit}
              onBack={wizard.backToRosterReview}
            />
          )}

          {wizard.step === 'importing' && (
            <ImportProgressBar
              done={importProgress?.done ?? 0}
              total={importProgress?.total ?? 0}
              imported={importProgress?.imported ?? 0}
              rejected={importProgress?.rejected ?? 0}
            />
          )}

          {wizard.step === 'done' && importResult && (
            <>
              {wizard.deflectedRowNumbers.length > 0 && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50/40 p-3 text-body-sm-regular text-foreground">
                  {wizard.deflectedRowNumbers.length} row{wizard.deflectedRowNumbers.length > 1 ? 's were' : ' was'}{' '}
                  kept at its existing roster value instead of the sheet's — the roster changed while this import was
                  under review, so nothing was overwritten:{' '}
                  {wizard.deflectedRowNumbers
                    .map((n) => {
                      const row = wizard.rows.find((r) => r.rowNumber === n);
                      return row?.values.username ? `@${row.values.username}` : `row ${n}`;
                    })
                    .join(', ')}
                  .
                </div>
              )}
              <ImportResultsPanel
                result={importResult}
                onReset={() => { wizard.reset(); onClose(); }}
                onUploadAnother={() => { wizard.reset(); wizard.checkStatus().catch(() => {}); }}
                suppressAutoDismiss={wizard.deflectedRowNumbers.length > 0}
              />
            </>
          )}
        </div>
        </ImportWizardFooterProvider>
      </DialogContent>
    </Dialog>
  );
}
